/**
 * Reusable data table component for paid media dashboards.
 * Handles column rendering, sorting, pagination, and loading states.
 * Can be composed inside any dashboard page layout alongside summary cards, export buttons, etc.
 */
import React from 'react';
import Column from '@amzn/meridian/column';
import Row from '@amzn/meridian/row';
import Text from '@amzn/meridian/text';
import Loader from '@amzn/meridian/loader';
import DataTable, {
  DataTableBody,
  DataTableCell,
  DataTableHead,
  DataTableHeader,
  DataTableRow,
} from '@amzn/meridian/data-table';
import { type SortDirection, type OnSort } from '@amzn/meridian/data-table/types/column-sort';
import { TablePagination } from 'src/components/common/data-table-action-bar/TablePagination';
import { TooltipHeader } from 'src/components/common/TooltipHeader';
import type { PaidMediaColumnDef } from 'src/store/leela-api';

import { defaultFormatCell } from 'src/utils/dashboardUtils';

const DEFAULT_COLUMN_WIDTH = 200;

interface DashboardTableProps {
  columns: PaidMediaColumnDef[];
  rows: Array<Record<string, unknown>>;
  columnWidths?: Record<string, number>;
  columnTooltips?: Record<string, string>;
  formatOverrides?: Record<string, (value: unknown) => string>;
  page: number;
  pageSize: number;
  pageSizeOptions?: number[];
  pageCount: number;
  totalResults: number;
  sortBy?: string;
  sortOrder?: SortDirection;
  isLoadingPage: boolean;
  onSort: OnSort;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
}

export const DashboardTable: React.FC<DashboardTableProps> = ({
  columns,
  rows,
  columnWidths = {},
  columnTooltips = {},
  formatOverrides = {},
  page,
  pageSize,
  pageSizeOptions,
  pageCount,
  totalResults,
  sortBy,
  sortOrder,
  isLoadingPage,
  onSort,
  onPageChange,
  onPageSizeChange,
}) => (
  <Column spacing="400">
    <Row alignmentVertical="center" spacing="300">
      <Text color="secondary" data-testid="dashboard-showing-count" type="b300">
        Showing {Math.min((page - 1) * pageSize + 1, totalResults)}-{Math.min(page * pageSize, totalResults)} of {totalResults}
      </Text>
      {isLoadingPage && <Loader size="small" type="linear" />}
    </Row>
    <div data-testid="dashboard-table-container" style={{ overflowX: 'auto', width: '100%' }}>
      <DataTable data-testid="dashboard-table" layout="fixed" showBorder={true} showColumnDividers={true} showRowDividers={true} size="medium" sortColumn={sortBy} sortDirection={sortBy ? sortOrder : undefined} stickyHeaderRows={true} onSort={onSort}>
        <DataTableHead backgroundColor="secondary">
          <DataTableRow>
            {columns.map((col) => (
              <DataTableHeader data-testid={`dashboard-col-${col.key}`} key={col.key} sortColumn={col.key} width={columnWidths[col.key] ?? DEFAULT_COLUMN_WIDTH}>
                <TooltipHeader label={col.label} tooltip={columnTooltips[col.key]} />
              </DataTableHeader>
            ))}
          </DataTableRow>
        </DataTableHead>
        <DataTableBody>
          {isLoadingPage
            ? Array.from({ length: pageSize }).map((_, i) => (
              <DataTableRow key={`skeleton-${i}`}>
                {columns.map((col) => (
                  <DataTableCell key={col.key}>
                    <div style={{ width: '80%', height: '1em', borderRadius: 4, backgroundColor: '#E9EBEF' }} />
                  </DataTableCell>
                ))}
              </DataTableRow>
            ))
            : (
            <>
              {rows.map((row, idx) => (
                <DataTableRow key={idx}>
                  {columns.map((col) => {
                    const value = formatOverrides[col.key] ? formatOverrides[col.key](row[col.key]) : defaultFormatCell(row[col.key], col.format);
                    const isNumeric = col.format === 'integer' || col.format === 'decimal';
                    return (
                      <DataTableCell key={col.key}>
                        <span style={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', textAlign: isNumeric ? 'right' : 'left' }} title={String(value)}>{value}</span>
                      </DataTableCell>
                    );
                  })}
                </DataTableRow>
              ))}
              {rows.length === 0 && (
                <DataTableRow>
                  <DataTableCell colSpan={columns.length || 1}>
                    <Text alignment="left">No data available</Text>
                  </DataTableCell>
                </DataTableRow>
              )}
            </>
              )}
        </DataTableBody>
      </DataTable>
    </div>
    <TablePagination
      currentPage={page}
      itemsPerPage={pageSize}
      itemsPerPageOptions={pageSizeOptions}
      pageCount={pageCount}
      onItemsPerPageChange={onPageSizeChange}
      onPageChange={onPageChange}
    />
  </Column>
);
